import { Controller } from "@hotwired/stimulus"
import $ from "jquery"
import "parsleyjs"

export default class extends Controller {
  static values = {
    errorClass: { type: String, default: "border-red-500" },
    successClass: { type: String, default: "border-green-500" },
    errorsWrapper: { type: String, default: '<ul class="parsley-errors-list mt-1"></ul>' },
    errorTemplate: { type: String, default: '<li class="text-red-600 text-sm"></li>' }
  }

  connect() {
    this.convertDataAttributes()
    this.initializeParsley()
    this.setupCheckboxGroupValidation()
  }

  disconnect() {
    if (this.$form) {
      this.$form.parsley().destroy()
    }
  }

  convertDataAttributes() {
    const attributeMap = {
      'data-parsley_required_message': 'data-parsley-required-message',
      'data-parsley_minlength': 'data-parsley-minlength',
      'data-parsley_minlength_message': 'data-parsley-minlength-message',
      'data-parsley_maxlength': 'data-parsley-maxlength',
      'data-parsley_maxlength_message': 'data-parsley-maxlength-message',
      'data-parsley_type': 'data-parsley-type',
      'data-parsley_type_message': 'data-parsley-type-message',
      'data-parsley_excluded': 'data-parsley-excluded',
      'data-parsley_errors_container': 'data-parsley-errors-container',
      'data-parsley_checkboxgroup': 'data-parsley-checkboxgroup',
      'data-parsley_checkboxgroup_message': 'data-parsley-checkboxgroup-message'
    }

    this.element.querySelectorAll('[data-parsley_required_message], [data-parsley_minlength], [data-parsley_maxlength], [data-parsley_type], [data-parsley_excluded], [data-parsley_errors_container], [data-parsley_checkboxgroup], [data-parsley_checkboxgroup_message]').forEach(el => {
      Object.entries(attributeMap).forEach(([oldAttr, newAttr]) => {
        if (el.hasAttribute(oldAttr)) {
          el.setAttribute(newAttr, el.getAttribute(oldAttr))
        }
      })
    })
  }

  initializeParsley() {
    this.$form = $(this.element)

    this.$form.parsley({
      errorClass: this.errorClassValue,
      successClass: this.successClassValue,
      errorsWrapper: this.errorsWrapperValue,
      errorTemplate: this.errorTemplateValue,
      classHandler: (field) => {
        return field.$element
      },
      errorsContainer: (field) => {
        const $element = field.$element
        // Check for custom errors container attribute
        const customContainer = $element.attr('data-parsley-errors-container')
        if (customContainer) {
          return $(customContainer)
        }
        const $wrapper = $element.closest('.field-wrapper')
        if ($wrapper.length) {
          return $wrapper
        }
        return $element.parent()
      },
      excluded: 'input[type=hidden]:not([data-parsley-required]), :disabled, [data-parsley-excluded]'
    })

    this.setupCustomValidators()
    this.setupEventListeners()
  }

  setupCustomValidators() {
    window.Parsley.addValidator('url', {
      validateString: function(value) {
        if (!value) return true
        try {
          new URL(value.startsWith('http') ? value : `https://${value}`)
          return true
        } catch {
          return false
        }
      },
      messages: {
        en: 'Please enter a valid URL'
      }
    })

    window.Parsley.addValidator('minselected', {
      validateMultiple: function(values, requirement) {
        return values.length >= requirement
      },
      messages: {
        en: 'Please select at least %s option(s)'
      }
    })

    // Custom validator for checkbox groups (languages)
    window.Parsley.addValidator('checkboxgroup', {
      validateString: function(_value, requirement, instance) {
        const groupName = instance.$element.attr('name')
        const form = instance.$element.closest('form')
        const checkedCount = form.find(`input[name="${groupName}"]:checked`).length
        return checkedCount >= parseInt(requirement)
      },
      messages: {
        en: 'Please select at least %s option(s)'
      }
    })
  }

  setupEventListeners() {
    this.$form.on('field:validated', (event) => {
      const field = event.parsley
      if (!field.$element) return

      const $input = field.$element
      $input.removeClass(this.errorClassValue).removeClass(this.successClassValue)

      if (field.isValid()) {
        $input.addClass(this.successClassValue)
      } else {
        $input.addClass(this.errorClassValue)
      }
    })

    this.$form.on('form:submit', () => {
      return true
    })

    this.$form.on('form:error', () => {
      const $firstError = this.$form.find(`.${this.errorClassValue}`).first()
      if ($firstError.length) {
        $firstError[0].scrollIntoView({ behavior: 'smooth', block: 'center' })
        $firstError.focus()
      }
    })
  }

  validate(event) {
    const isValid = this.$form.parsley().validate()
    if (!isValid) {
      event.preventDefault()
    }
    return isValid
  }

  reset() {
    this.$form.parsley().reset()
    this.$form.find('input, select, textarea').removeClass(this.errorClassValue).removeClass(this.successClassValue)
  }

  setupCheckboxGroupValidation() {
    // Find all hidden validators for checkbox groups
    const validators = this.element.querySelectorAll('input[type="hidden"][data-parsley-required][data-checkbox-group]')

    validators.forEach(validator => {
      const groupName = validator.dataset.checkboxGroup
      const checkboxes = this.element.querySelectorAll(`input[type="checkbox"][name="${groupName}"]`)

      checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', () => {
          this.updateCheckboxGroupValidator(validator, groupName)
        })
      })
    })
  }

  updateCheckboxGroupValidator(validator, groupName) {
    const checkboxes = this.element.querySelectorAll(`input[type="checkbox"][name="${groupName}"]`)
    const checkedCount = Array.from(checkboxes).filter(cb => cb.checked).length

    validator.value = checkedCount > 0 ? "1" : ""

    // Trigger Parsley validation
    const $validator = $(validator)
    if ($validator.parsley()) {
      $validator.parsley().validate()
    }
  }
}
